import TabPanel from 'client/components/GDS/Tabs/TabPanel/TabPanel';
import ContactDetails from 'client/pages/MapPage/components/MapMenu/ContactDetails/ContactDetails';
import Loading from 'client/components/ui/Loading/Loading';
import Button from 'client/components/aria/Button/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styles from './ContactDetailsTab.module.scss';
import { getContactSections } from 'client/utils/helpers/SupplementaryData/SupplementaryDataUtils';

// Shown in test ids so the origin of each block is visible when inspecting the DOM
const SUPPLEMENTARY_DATA_SOURCE = 'supplementaryData';
const ASSET_OWNER_SERVICE_SOURCE = 'assetOwnerService';
const DATA_SOURCE_BY_SECTION_TYPE = {
    asset: SUPPLEMENTARY_DATA_SOURCE,
    assetOwner: ASSET_OWNER_SERVICE_SOURCE
};

const CONTACT_TYPES = [
    { type: 'business', captionId: 'contactDetails.businessContact' },
    { type: 'emergency', captionId: 'contactDetails.emergencyContact' }
];

export const ContactDetailsTab = ({ label, assetOwnerName, supplementaryDataEntries, assetContacts, ownerContacts, isContactsLoading, hasContactsError, onRetryContacts }) => {
    const intl = useIntl();

    // Asset contacts come from supplementary data, asset owner contacts from the asset owner service
    const contactSections = getContactSections({
        supplementaryDataEntries,
        assetOwnerName,
        assetContacts,
        ownerContacts,
        supplementaryContactsSource: null
    });

    const renderContacts = (section, index) => {
        // CONTACT_TYPES order keeps business before emergency
        return CONTACT_TYPES.map(({ type, captionId }) => (
            section.contacts[type]?.length
                ? <ContactDetails
                    key={type}
                    contacts={section.contacts[type]}
                    caption={intl.formatMessage({ id: captionId })}
                    type={type}
                    variant="simple"
                    data-testid={`contactDetailsTab.${DATA_SOURCE_BY_SECTION_TYPE[section.sourceType]}.${section.sourceType}.${type}.${index}`}
                />
                : null
        ));
    };

    // Asset owner sections are headed by the owner name, falling back to the generic heading
    const getContactSectionHeading = (section) => {
        return section.heading || intl.formatMessage({ id: `contactDetailsTab.${section.sourceType}Heading` });
    };

    const renderContactSection = (section, index) => {
        const dataSource = DATA_SOURCE_BY_SECTION_TYPE[section.sourceType];

        return (
            <section
                key={`${section.sourceType}-${index}`}
                data-testid={`contactDetailsTab.${dataSource}.section.${section.sourceType}.${index}`}
            >
                <h4 className="govuk-heading-s" data-testid={`contactDetailsTab.${dataSource}.sectionHeading.${section.sourceType}.${index}`}>
                    {getContactSectionHeading(section)}
                </h4>
                {renderContacts(section, index)}
            </section>
        );
    };

    const renderContactDetails = () => {
        if (hasContactsError) {
            return (
                <div className={styles.error} data-testid="contactDetailsTab.contactDetailsError">
                    <p className="govuk-body govuk-!-font-size-16 govuk-!-margin-0">
                        <FormattedMessage id="contactDetailsTab.contactDetailsError" />
                    </p>
                    <Button
                        variant='default'
                        data-testid="contactDetailsTab.contactDetailsErrorRetryButton"
                        className={styles.retryButton}
                        onPress={onRetryContacts}>
                        <FormattedMessage id="contactDetailsTab.contactDetailsError.button" />
                    </Button>
                </div>
            );
        }

        if (isContactsLoading) {
            return <Loading data-testid="contactDetailsTab.contactDetailsLoading" variant="compact" />;
        }

        return null;
    };

    const getSectionsOfType = (sourceType) => contactSections.filter(section => section.sourceType === sourceType);
    const assetContactSections = getSectionsOfType('asset');
    const assetOwnerContactSections = getSectionsOfType('assetOwner');

    return <TabPanel data-testid="contactDetailsTab" label={label}>
        <div className={styles.container}>
            {renderContactDetails()}
            {assetContactSections.length > 0 && (
                <section className={styles.assetSection} data-testid={`contactDetailsTab.${SUPPLEMENTARY_DATA_SOURCE}.assetContactSection`}>
                    <h4 className="govuk-heading-s" data-testid={`contactDetailsTab.${SUPPLEMENTARY_DATA_SOURCE}.assetContactSectionHeading`}>
                        {intl.formatMessage({ id: 'contactDetailsTab.assetHeading' })}
                    </h4>
                    {assetContactSections.map((section, index) => (
                        <div key={`${section.sourceType}-${index}`}>
                            {renderContacts(section, index)}
                        </div>
                    ))}
                </section>
            )}
            {assetOwnerContactSections.map(renderContactSection)}
        </div>
    </TabPanel>;
};

ContactDetailsTab.propTypes = {
    label: PropTypes.string.isRequired,
    assetOwnerName: PropTypes.string,
    supplementaryDataEntries: PropTypes.array,
    assetContacts: PropTypes.object,
    ownerContacts: PropTypes.object,
    isContactsLoading: PropTypes.bool,
    hasContactsError: PropTypes.bool,
    onRetryContacts: PropTypes.func
};
